import { useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from './ThemeProvider';
import { ThemeToggle } from './ThemeToggle';

const navClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-md px-2 py-1.5 text-xs font-medium transition-colors duration-200 lg:px-2.5 lg:text-[13px] ${
    isActive ? 'bg-ct-accent/15 text-ct-accent' : 'text-ct-muted hover:bg-ct-elevated hover:text-ct-text'
  }`;

const moreItemClass =
  'block rounded-lg px-3 py-2 text-sm font-medium text-ct-text hover:bg-ct-elevated';

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDetailsElement>(null);
  const { user, logout } = useAuth();
  const { resolvedScheme } = useTheme();
  const headerLogo =
    resolvedScheme === 'light' ? '/branding/logo-header-light.png' : '/branding/logo-header-dark.png';

  return (
    <header className="print:hidden sticky top-0 z-50 border-b border-ct-border bg-ct-surface/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3">
        <Link
          to="/"
          className="flex min-w-0 shrink items-center gap-2 rounded-xl text-ct-text transition-opacity duration-200 hover:opacity-90 sm:gap-2.5"
          onClick={() => setOpen(false)}
        >
          <img
            src={headerLogo}
            alt="ComfortTool™"
            className="h-8 w-auto max-w-[160px] object-contain object-left sm:max-w-[180px] md:h-9 lg:max-w-[200px]"
            width={200}
            height={40}
            decoding="async"
          />
          <span className="hidden text-[11px] font-semibold leading-tight text-ct-muted xl:inline xl:max-w-[10.5rem]">
            Thuis &amp; professional · indicatief
          </span>
        </Link>

        <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
          <nav className="hidden items-center md:flex md:gap-0.5 lg:gap-1" aria-label="Hoofdmenu">
            <NavLink to="/" className={navClass} end>
              Home
            </NavLink>
            <NavLink to="/scan" className={navClass}>
              Scan
            </NavLink>
            <NavLink to="/demo" className={navClass}>
              Demo
            </NavLink>
            <NavLink to="/prijzen" className={navClass}>
              Prijzen
            </NavLink>
            <NavLink to="/faq" className={navClass}>
              FAQ
            </NavLink>
            <NavLink to="/reviews" className={navClass}>
              Reviews
            </NavLink>
            <details ref={moreMenuRef} className="relative group">
              <summary
                className={`${navClass({ isActive: false })} flex cursor-pointer list-none items-center gap-0.5 [&::-webkit-details-marker]:hidden`}
              >
                Meer
                <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />
              </summary>
              <div
                className="absolute right-0 top-full z-[60] mt-1 min-w-[12rem] rounded-xl border border-ct-border bg-ct-surface py-1 shadow-xl ring-1 ring-black/5 dark:ring-white/10"
                role="menu"
              >
                <NavLink
                  to="/kennisbank"
                  className={moreItemClass}
                  role="menuitem"
                  onClick={() => moreMenuRef.current?.removeAttribute('open')}
                >
                  Kennisbank
                </NavLink>
                <NavLink
                  to="/help"
                  className={moreItemClass}
                  role="menuitem"
                  onClick={() => moreMenuRef.current?.removeAttribute('open')}
                >
                  Help
                </NavLink>
                <NavLink
                  to="/contact"
                  className={moreItemClass}
                  role="menuitem"
                  onClick={() => moreMenuRef.current?.removeAttribute('open')}
                >
                  Contact
                </NavLink>
                <NavLink
                  to="/over-ons"
                  className={moreItemClass}
                  role="menuitem"
                  onClick={() => moreMenuRef.current?.removeAttribute('open')}
                >
                  Over ons
                </NavLink>
              </div>
            </details>
            {user ? (
              <>
                <NavLink to="/account" className={navClass}>
                  Account
                </NavLink>
                {user.is_superadmin ? (
                  <NavLink to="/admin" className={navClass}>
                    Admin
                  </NavLink>
                ) : null}
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="rounded-md px-2 py-1.5 text-xs font-medium text-ct-muted hover:bg-ct-elevated hover:text-ct-text lg:text-[13px]"
                >
                  Uitloggen
                </button>
              </>
            ) : (
              <NavLink to="/inloggen" className={navClass}>
                Inloggen
              </NavLink>
            )}
          </nav>

          <ThemeToggle className="hidden sm:inline-flex" />

          <button
            type="button"
            className="rounded-lg border border-ct-border p-2 text-ct-text md:hidden"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'Menu sluiten' : 'Menu openen'}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </button>
        </div>
      </div>

      {open ? (
        <nav
          id="mobile-nav"
          className="border-t border-ct-border bg-ct-surface px-4 py-3 md:hidden"
          aria-label="Mobiel menu"
        >
          <div className="flex flex-col gap-1">
            <NavLink to="/" className={navClass} end onClick={() => setOpen(false)}>
              Home
            </NavLink>
            <NavLink to="/scan" className={navClass} onClick={() => setOpen(false)}>
              Scan
            </NavLink>
            <NavLink to="/demo" className={navClass} onClick={() => setOpen(false)}>
              Demo
            </NavLink>
            <NavLink to="/prijzen" className={navClass} onClick={() => setOpen(false)}>
              Prijzen
            </NavLink>
            <NavLink to="/faq" className={navClass} onClick={() => setOpen(false)}>
              FAQ
            </NavLink>
            <NavLink to="/reviews" className={navClass} onClick={() => setOpen(false)}>
              Reviews
            </NavLink>
            <NavLink to="/kennisbank" className={navClass} onClick={() => setOpen(false)}>
              Kennisbank
            </NavLink>
            <NavLink to="/over-ons" className={navClass} onClick={() => setOpen(false)}>
              Over ons
            </NavLink>
            <NavLink to="/troubleshooting" className={navClass} onClick={() => setOpen(false)}>
              Troubleshooting
            </NavLink>
            <NavLink to="/help" className={navClass} onClick={() => setOpen(false)}>
              Help
            </NavLink>
            <NavLink to="/contact" className={navClass} onClick={() => setOpen(false)}>
              Contact
            </NavLink>
            {user ? (
              <>
                <NavLink to="/account" className={navClass} onClick={() => setOpen(false)}>
                  Account
                </NavLink>
                {user.is_superadmin ? (
                  <NavLink to="/admin" className={navClass} onClick={() => setOpen(false)}>
                    Admin
                  </NavLink>
                ) : null}
                <button
                  type="button"
                  className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-ct-muted hover:bg-ct-elevated"
                  onClick={() => {
                    setOpen(false);
                    void logout();
                  }}
                >
                  Uitloggen
                </button>
              </>
            ) : (
              <NavLink to="/inloggen" className={navClass} onClick={() => setOpen(false)}>
                Inloggen
              </NavLink>
            )}
            <div className="pt-2 sm:hidden">
              <ThemeToggle className="w-full justify-center" />
            </div>
          </div>
        </nav>
      ) : null}
    </header>
  );
}
